import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client.js'
import { endpoints } from '../../api/endpoints.js'
import { unwrapList } from '../../utils/format.js'
import { useDebounce } from '../../hooks/useDebounce.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import CatalogueImageThumb from '../../components/CatalogueImageThumb.jsx'

/**
 * Patient-facing lab marketplace.
 *
 * Search the global lab catalogue → expand a row to see every laboratory
 * that currently offers it, with price + turnaround time. Sorted
 * cheapest-first so a patient can quickly see "where is FBC cheapest".
 *
 * Patients can't directly place lab orders — those flow through a doctor
 * via the CNE. The page therefore ends each expansion with a "Discuss
 * with a doctor" link to the booking flow.
 */
export default function PatientLabBrowser() {
  const [q, setQ] = useState('')
  const [category, setCategory] = useState('')
  const debounced = useDebounce(q, 250)
  const [openId, setOpenId] = useState(null)

  const CATEGORIES = ['', 'Haematology', 'Biochemistry', 'Microbiology',
                      'Serology', 'Endocrine', 'Coagulation', 'Tumour markers',
                      'Immunology', 'Urinalysis', 'Stool', 'Point of care']

  const testsQ = useQuery({
    queryKey: ['lab-tests', debounced, category],
    queryFn: async () => {
      const params = {}
      if (debounced.trim().length >= 2) params.search = debounced.trim()
      if (category) params.category = category
      const { data } = await api.get(endpoints.labTestSearch, { params })
      return unwrapList(data)
    },
    enabled: debounced.trim().length >= 2 || debounced === '' || Boolean(category),
    staleTime: 60_000,
  })
  const rows = testsQ.data || []

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Lab tests marketplace"
          subtitle="Browse the lab tests Wantok-network laboratories offer. Pick a test to see who runs it, how much it costs, and how long results take. Tests are ordered through your doctor — book a consultation if you need one."
          right={
            <Link to="/patient" className="text-xs text-wantok-700 underline">
              ← Back to my home
            </Link>
          }
        />
        <div className="flex flex-wrap gap-2">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='Search test name (e.g. "FBC", "malaria RDT", "HbA1c")…'
            className="flex-1 min-w-[14rem] border rounded px-3 py-2 text-sm"
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="border rounded px-3 py-2 text-sm bg-white"
          >
            {CATEGORIES.map((c) => (
              <option key={c || 'all'} value={c}>{c || 'All categories'}</option>
            ))}
          </select>
        </div>
      </Card>

      {testsQ.error && <ErrorBanner error={testsQ.error} onRetry={() => testsQ.refetch()} />}

      {testsQ.isLoading && (
        <div className="text-sm text-slate-400 text-center py-6">Searching…</div>
      )}

      {!testsQ.isLoading && debounced.trim().length === 0 && !category && (
        <Card>
          <div className="text-sm text-slate-500">
            Type a test name above (or pick a category) to see what's offered
            and where. Tests are ordered through your doctor — once they
            request a test we'll route it to whichever lab you choose.
          </div>
        </Card>
      )}

      {!testsQ.isLoading && (debounced.trim().length > 0 || category) && rows.length === 0 && (
        <Card>
          <div className="text-sm text-slate-500">
            No lab tests match your filters. Try broader keywords or a
            different category.
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map((t) => (
          <LabTestRow
            key={t.id}
            test={t}
            expanded={openId === t.id}
            onToggle={() => setOpenId(openId === t.id ? null : t.id)}
          />
        ))}
      </div>
    </div>
  )
}

function LabTestRow({ test, expanded, onToggle }) {
  return (
    <Card>
      <button
        onClick={onToggle}
        className="w-full text-left flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3 min-w-0">
          <CatalogueImageThumb url={test.image_url} alt={test.test_name} />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800">
              {test.test_name}
              {test.short_code && (
                <span className="ml-2 text-[10px] uppercase text-slate-500 font-mono">
                  {test.short_code}
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-2">
              {test.category && <span>{test.category}</span>}
              {test.sample_type && <span>· {test.sample_type}</span>}
              {test.typical_turnaround && <span>· TAT {test.typical_turnaround}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {test.indicative_price_pgk != null && (
            <span className="text-[11px] text-slate-500">
              from PGK {test.indicative_price_pgk}
            </span>
          )}
          <span className="text-wantok-700 text-sm">
            {expanded ? '▾' : '▸'}
          </span>
        </div>
      </button>
      {expanded && <LabAvailability testId={test.id} testName={test.test_name} />}
    </Card>
  )
}

function LabAvailability({ testId, testName }) {
  const stockQ = useQuery({
    queryKey: ['lab-test-stock', testId],
    queryFn: async () => {
      const { data } = await api.get(`${endpoints.labTestSearch}${testId}/stock/`)
      return data
    },
    staleTime: 30_000,
  })
  const rows = stockQ.data || []

  if (stockQ.isLoading) {
    return <div className="mt-3 text-sm text-slate-400">Loading availability…</div>
  }
  if (stockQ.error) {
    return <div className="mt-3"><ErrorBanner error={stockQ.error} /></div>
  }
  if (rows.length === 0) {
    return (
      <div className="mt-3 text-sm text-slate-500 italic">
        No laboratory in the network is currently offering <b>{testName}</b>.
        Your doctor may suggest a similar test — book a consultation to ask.
      </div>
    )
  }

  // Sort cheapest first; rows without a price drop to the bottom.
  const sorted = [...rows].sort((a, b) => {
    const ap = a.price ?? Infinity
    const bp = b.price ?? Infinity
    return ap - bp
  })

  return (
    <div className="mt-3 border-t pt-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
        {sorted.length} laborator{sorted.length === 1 ? 'y' : 'ies'} offering this — cheapest first
      </div>
      <ul className="divide-y divide-slate-100">
        {sorted.map((row) => (
          <li key={row.id} className="py-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <CatalogueImageThumb
                url={row.laboratory_logo_url}
                alt={row.laboratory_name}
                size={32}
              />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {row.laboratory_name}
                </div>
                <div className="text-[11px] text-slate-500 flex flex-wrap gap-2 mt-0.5">
                  {row.turnaround_time && <span>TAT {row.turnaround_time}</span>}
                  {row.notes && <span className="truncate">· {row.notes}</span>}
                </div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm font-semibold text-slate-800">
                {row.price != null ? `PGK ${row.price}` : '—'}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-3 text-[11px] text-slate-500 flex items-center justify-between">
        <span>Tests are ordered through your doctor.</span>
        <Link to="/patient/book" className="text-wantok-700 underline">
          Book a consultation →
        </Link>
      </div>
    </div>
  )
}
